/**
 * The password policy's one number and its wording, shared by the user pool (infrastructure/central auth.ts) and the
 * invitation. Ported from gairm/lms e0d8eb7 (shared/types), where it was proven with Learn's own sign-in screens.
 */
export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_RULES = `at least ${PASSWORD_MIN_LENGTH} characters, with an upper-case letter, a lower-case letter, a number and a symbol`;

/**
 * The pool's password policy as data (auth-self-hosted-sign-in R2; gairm/lms#393): the user pool is built from it (infrastructure auth.ts), the portal
 * stack writes it into config.json, and Starsach's own new-password screens state it before the person types and name each
 * rule a password does not meet yet.
 */
export interface PasswordPolicy {
  minLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireDigits: boolean;
  requireSymbols: boolean;
}
export const PASSWORD_POLICY: PasswordPolicy = { minLength: PASSWORD_MIN_LENGTH, requireLowercase: true, requireUppercase: true, requireDigits: true, requireSymbols: true };

export type PasswordRule = "length" | "lowercase" | "uppercase" | "digit" | "symbol" | "edge-space";

/** Cognito's special characters, the only ones its "symbol" rule counts (its documentation's list; a space in the middle counts too). */
const COGNITO_SYMBOLS = new Set([..."^$*.[]{}()?\"!@#%&/\\,><':;|_~`=+- "]);

/** Each rule a policy states, in order, in the words the screens use. `edge-space` is not stated: it shows only when broken. */
export function passwordRuleList(policy: PasswordPolicy): Array<{ rule: PasswordRule; text: string }> {
  return [
    { rule: "length" as const, text: `At least ${policy.minLength} characters` },
    ...(policy.requireUppercase ? [{ rule: "uppercase" as const, text: "An upper-case letter (A to Z)" }] : []),
    ...(policy.requireLowercase ? [{ rule: "lowercase" as const, text: "A lower-case letter (a to z)" }] : []),
    ...(policy.requireDigits ? [{ rule: "digit" as const, text: "A number (0 to 9)" }] : []),
    ...(policy.requireSymbols ? [{ rule: "symbol" as const, text: "A symbol, such as ! ? # or -" }] : []),
  ];
}

/** The rules `password` does not meet yet, as Cognito would judge them (it also refuses a space at either end). */
export function passwordProblems(password: string, policy: PasswordPolicy): PasswordRule[] {
  const chars = [...password];
  const out: PasswordRule[] = [];
  if (chars.length < policy.minLength) out.push("length");
  if (policy.requireUppercase && !/[A-Z]/.test(password)) out.push("uppercase");
  if (policy.requireLowercase && !/[a-z]/.test(password)) out.push("lowercase");
  if (policy.requireDigits && !/[0-9]/.test(password)) out.push("digit");
  // A space counts as a symbol in the middle only; at either end it is refused outright (edge-space).
  if (policy.requireSymbols && !chars.some((c, i) => COGNITO_SYMBOLS.has(c) && (c !== " " || (i > 0 && i < chars.length - 1)))) out.push("symbol");
  if (password.length > 0 && password.trim() !== password) out.push("edge-space");
  return out;
}
/**
 * The pool's verification email — the one Cognito sends for *Forgot your password?* and for confirming an email address
 * — shared by the pool (infrastructure auth.ts, `userVerification`) and the screens that ask for its code
 * (gairm/lms#394), so a screen names the email the person will find. Starsach's CustomEmailSender writes its own
 * words (messages.ts); this subject is what the screens name when the pool sends it itself.
 */
export const VERIFICATION_EMAIL_SUBJECT = "Your Starsach verification code";
export const VERIFICATION_EMAIL_LEAD = "Your verification code is on the next line by itself:";

/** Days a temporary password stays valid (the pool's `tempPasswordValidity`). */
export const TEMPORARY_PASSWORD_DAYS = 3;
